import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { CinemaType, MovieType } from "../../types/entities";
import { getDateFilterDisplayString } from "../../utils/showtimes";
import ShowtimeButton from "../common/Showtimes/ShowtimeButton";
import Button from "../Button";
import { getBootstrapFilters, getCheckoutCinema, getCheckoutMovie } from "../../reducers";
import { CHECKOUT_OTHER_SHOWTIMES_FETCH } from "../../actions/types";
import { getCheckoutOtherShowtimes } from "../../reducers/checkout/otherShowtimes";
import styled from "styled-components";

const Scroller = styled.div`
    position: relative;
    overflow-x: auto;
    width: 100%;
    user-select: none;
    margin-bottom: 4rem;
    
    &::-webkit-scrollbar {
        width: 0.5rem;
        height: 0.5rem;
    }

    &::-webkit-scrollbar-track { background: #fcfcfc; /* Color of the track */ }
    &::-webkit-scrollbar-thumb { background: #eee; /* Color of the thumb */ }
    &::-webkit-scrollbar-thumb:hover { background: #ddd; /* Darker color on hover */ }

    body.theme-market & { 
        &::-webkit-scrollbar-track { background: #333; /* Color of the track */ }
        &::-webkit-scrollbar-thumb { background: #666; /* Color of the thumb */ }
        &::-webkit-scrollbar-thumb:hover { background: #777; /* Darker color on hover */ }
    }

    body.theme-platinum & { 
        &::-webkit-scrollbar-track { background: var(--primary-b); /* Color of the track */ }
        &::-webkit-scrollbar-thumb { background: var(--primary); /* Color of the thumb */ }
        &::-webkit-scrollbar-thumb:hover { background: var(--primary); /* Darker color on hover */ }
    }
`;

const DayGrid = styled.div`
    display: flex;
    gap: 0.5rem;
    width: max-content;
`;

const DayBlock = styled.div`
    display: inline-flex;
    align-items: stretch;
    gap: 0.5rem;
    position: relative;
    min-width: max-content;
`;

const StickyDayButton = styled.div`
    display: flex;
    align-items: center;
    background: var(--page-bg);
    height: 100%;
    left: 0;
    position: sticky;
    z-index: 2;

    & button {
        height: 100%;
    }
`;

const ShowtimeButtonWrapper = styled.div`
    display: inline-flex;
    align-items: stretch;

    & > div {
        display: flex;
        align-items: center;
        height: 100%;
        min-width: 7rem !important;
    }

    & button, & a {
        height: 100%;
    }
`;

interface Props {
    attributes: { [key: string]: { display_name: string } };
    cinema: CinemaType;
    currentSessionId: number;
    data?: { day: string; sessions: any[] }[] | null;
    fetch: () => void;
    movie: MovieType;
}

function ShowtimesSelector(props: Props) {
    const { attributes, cinema, currentSessionId, data, fetch, movie } = props;
    const scrollerRef = useRef<HTMLDivElement | null>(null);
    const buttonRefs = useRef<{ [id: string]: HTMLDivElement | null }>({});
    const setScrollerRef = useCallback((node: HTMLDivElement | null) => {
        scrollerRef.current = node;
    }, []);
    const setButtonRef = useCallback((sessionId: number | string) => (node: HTMLDivElement | null) => {
        buttonRefs.current[sessionId] = node;
    }, []);

    const langs = useMemo(() => {
        if (!movie) {
            return [] as string[];
        }

        const attributesList = Array.isArray(movie.attributes) ? movie.attributes : [];
        return attributesList.filter(attr => attr.startsWith("lang_"));
    }, [movie]);

    useEffect(() => {
        const hasData = !!data;

        if (hasData || !cinema || !movie) {
            return;
        }

        fetch();
    }, [cinema, movie, data, fetch]);

    useLayoutEffect(() => {
        if (!currentSessionId || !data || !data.length) {
            return;
        }

        const scroller = scrollerRef.current;
        const button = buttonRefs.current[currentSessionId];

        if (!scroller || !button) {
            return;
        }

        const scrollerRect = scroller.getBoundingClientRect();
        const buttonRect = button.getBoundingClientRect();
        const currentScroll = scroller.scrollLeft;
        const buttonCenter = buttonRect.left - scrollerRect.left + currentScroll + buttonRect.width / 2;
        const targetScroll = Math.max(0, buttonCenter - scrollerRect.width / 2);
        scroller.scrollTo({ left: targetScroll });
    }, [currentSessionId, data]);

    const today = useMemo(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    }, []);

    const title = useMemo(() => getTitle(attributes, langs, movie, cinema), [attributes, langs, movie, cinema]);

    if (!data || !data.length) {
        return null;
    }

    return <>
        {title}
        <div className="showtimes-selector">
            <Scroller innerRef={setScrollerRef}>
                <DayGrid>
                    {data.map(dayData => (
                        <DayBlock key={dayData.day}>
                            <StickyDayButton>
                                <Button primary={dayData.day === today} secondary={dayData.day !== today}>{getDateFilterDisplayString(dayData.day)}</Button>
                            </StickyDayButton>
                            {dayData.sessions.map(item => (
                                <ShowtimeButtonWrapper
                                    key={item.session.id}
                                    innerRef={setButtonRef(item.session.id)}
                                >
                                    <ShowtimeButton session={item.session} version={item.version} movieName={movie.name} cinema={cinema} disabled={item.session.id === currentSessionId} />
                                </ShowtimeButtonWrapper>
                            ))}
                        </DayBlock>
                    ))}
                </DayGrid>
            </Scroller>
        </div>
    </>;
}

function getTitle(attributes: { [key: string]: { display_name: string } }, langs: string[], movie: MovieType, cinema: CinemaType) {
    if (!langs.length) {
        return null;
    }

    const displayNames = langs.map(lang => attributes[lang] ? attributes[lang].display_name : lang);
    return <p className="mb-3">Otras sesiones <strong>({movie.name} - {displayNames.join(', ')} - {cinema.name})</strong></p>;
}

const mapStateToProps = (state: any) => ({
    attributes: getBootstrapFilters(state),
    data: getCheckoutOtherShowtimes(state),
    movie: getCheckoutMovie(state),
    cinema: getCheckoutCinema(state),
});

const mapDispatchToProps = (dispatch: any) => ({
    fetch: () => dispatch({ type: CHECKOUT_OTHER_SHOWTIMES_FETCH }),
});

export default React.memo(connect(
    mapStateToProps,
    mapDispatchToProps
)(ShowtimesSelector));